import React, {
  useState,
  lazy,
  Suspense,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet-async';
import Checkbox from '@mui/material/Checkbox';
import jwtDecode from 'jwt-decode';
import {
  Chip as MuiChip,
  Typography,
  Stack,
  Button,
  MenuItem,
  OutlinedInput,
  Select,
  ListItemText,
  TextField,
  Grid,
  CardContent,
} from '@mui/material';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import shadows from '@mui/material/styles/shadows';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { Box } from '@mui/system';
import { Form, Field, Formik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { useAuth } from '../../services/contexts/AuthContext';
import api from '../../Middleware/api/api';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useTranslation } from 'react-i18next';
import LogoLoader from '../../components/LogoLoader';
import { useTokenUtils } from '../../services/utils/tokenUtils';
import { View } from './downloadCenterStyles';
import {
  formatCpoIdWithName,
  translateChargingType,
  formatLocationIdWithName,
  DownloadCenterFieldRow,
} from './downloadCenterUtils';
import MuiSelect from '@mui/material/Select';
const Chip = styled(MuiChip)`
  height: 20px;
  padding: 4px 0;
  margin-left: 7px;
  font-size: 90%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : 'primary'].light};
  color: white !important;
`;

const FailFeedback = lazy(() => import('../../components/DataTable/Feedback'));

function calculatedates(days) {
  let date = new Date();
  let last = new Date(date.getTime() - days * 24 * 60 * 60 * 1000);
  return (
    last.getFullYear() +
    '-' +
    last.toLocaleString('en-US', { month: '2-digit' }) +
    '-' +
    last.toLocaleString('en-US', { day: '2-digit' })
  );
}

function DownloadChargingHistory({ stations, operators }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(calculatedates(7));
  const [endDate, setEndDate] = useState(calculatedates(0));
  const [
    ButtonDownloadHistoryIsSubmitting,
    setButtonDownloadHistoryIsSubmitting,
  ] = useState(false);
  const { token } = useAuth();
  // NORMAL --> app or rfid charging ( is shown on fe as regular --> do not be confused with regular contract)
  // AD_HOC --> ad hoc charging using webapp
  const chargingTypeModes = [
    {
      value: 'NORMAL',
      translationKey: 'LBLchargingTypeModeNormal',
    },
    { value: 'AD_HOC', translationKey: 'LBLchargingTypeModeAdHoc' },
  ];
  const [chargingTypeModeSelected, setChargingTypeModeSelected] =
    useState('NORMAL');
  const exportType = 'CSV';
  const initialValues = {
    startDate: startDate,
    endDate: endDate,
  };
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [error, setError] = useState(false);
  const [error204, setError204] = useState(false);
  const [error404, setError404] = useState(false);
  const validationSchema = useMemo(
    () =>
      Yup.object({
        startDate: Yup.date()
          .typeError(t('LBLInvalidDate'))
          .required(t('LBLReqCockpit')),
        endDate: Yup.date()
          .typeError(t('LBLInvalidDate'))
          .min(Yup.ref('startDate'), t('LBLENdgStart'))
          .required(t('LBLEndDateReq')),
      }),
    []
  );
  const isAllSelectedStations =
    stations?.length > 0 && selectedLocations?.length === stations?.length;

  const handleChargingTypeModeChange = (event) => {
    if (event.target.value !== undefined) {
      setChargingTypeModeSelected(event.target.value);
    }
  };

  const getFileName = (response) => {
    let filename = 'Ladehistorie.csv';
    const contentDisposition = response?.headers['content-disposition'];
    if (contentDisposition && contentDisposition.includes('filename=')) {
      filename = contentDisposition.split('filename=')[1].replace(/['"]/g, '');
    }
    return filename;
  };
  const exportChargingHistory = (startDateValue, endDateValue) => {
    const urlParams = new URLSearchParams();
    urlParams.set('exportType', exportType);
    urlParams.set('startDate', startDateValue);
    urlParams.set('endDate', endDateValue);
    urlParams.set('locationIds', selectedLocations?.toString());
    urlParams.set('chargingType', chargingTypeModeSelected);
    api
      .exportChargingHistory(token, urlParams)
      .then((response) => {
        if (response?.status == 404) {
          setError404(true);
        } else if (response?.status == 200) {
          if (response?.data) {
            let filename = getFileName(response);
            const url = window.URL.createObjectURL(new Blob([response?.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
          }
        } else {
          setError(true);
        }
        setButtonDownloadHistoryIsSubmitting(false);
        setLoading(false);
      })
      .catch((reason) => {
        if (reason?.response?.status === 404) {
          setError404(true);
        } else {
          setError(true);
        }
        setButtonDownloadHistoryIsSubmitting(false);
        setLoading(false);
      });
  };
  const handleTestLocationChange = (event) => {
    if (event.target.value !== undefined) {
      if (
        event.target.value.includes('all') &&
        selectedLocations?.length !== stations?.length
      ) {
        setSelectedLocations(stations?.map((station) => station?.locationId));
        setLocationSelectOpen(false);
      } else if (event.target.value.includes('all')) {
        setSelectedLocations([]);
      } else {
        setSelectedLocations(event.target.value);
      }
    }
  };

  const handleFromDatePostingChange = (newValue, setFieldValue) => {
    if (
      newValue instanceof Date &&
      (!isNaN(newValue.getTime()) || !isNaN(newValue.toDate()))
    ) {
      setStartDate(format(newValue, 'yyyy-MM-dd'));
      setFieldValue('startDate', format(newValue, 'yyyy-MM-dd'));
    } else if (newValue != null && !isNaN(newValue.toDate())) {
      setStartDate(format(newValue.toDate(), 'yyyy-MM-dd'));
      setFieldValue('startDate', format(newValue.toDate(), 'yyyy-MM-dd'));
    } else {
      setStartDate(newValue);
      setFieldValue('startDate', newValue);
      console.error('Invalid FromDatePosting input');
    }
  };

  const handleUntilDatePostingChange = (newValue, setFieldValue) => {
    if (
      newValue instanceof Date &&
      (!isNaN(newValue.getTime()) || !isNaN(newValue.toDate()))
    ) {
      setEndDate(format(newValue, 'yyyy-MM-dd'));
      setFieldValue('endDate', format(newValue, 'yyyy-MM-dd'));
    } else if (newValue != null && !isNaN(newValue.toDate())) {
      setEndDate(format(newValue.toDate(), 'yyyy-MM-dd'));
      setFieldValue('endDate', format(newValue.toDate(), 'yyyy-MM-dd'));
    } else {
      setEndDate(newValue);
      setFieldValue('endDate', newValue);
      console.error('Invalid UntilDatePosting input');
    }
  };

  const handleSubmit = (values, actions) => {
    setStartDate(values.startDate);
    setEndDate(values.endDate);
    setButtonDownloadHistoryIsSubmitting(true);
    setLoading(true);
    exportChargingHistory(values.startDate, values.endDate);
    actions.setSubmitting(false);
  };
  const [locationSelectOpen, setLocationSelectOpen] = React.useState(false);
  return (
    <>
      <LogoLoader loading={loading} />
      <div>
        <div>
          <br />
          <Typography variant="h6" gutterBottom display="inline">
            {t('LBLDownloadHistoryP')}
          </Typography>
          <br />
          <br />
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnMount={true}
            onSubmit={handleSubmit}
          >
            {({ errors, values, setFieldValue }) => (
              <Form noValidate>
                <Grid container columnSpacing={2} rowSpacing={2}>
                  {/* LEFT COLUMN */}
                  <Grid item xs={12} md={6}>
                    <View>
                      <CardContent>
                        {/* Locations */}
                        <DownloadCenterFieldRow
                          label={t('MenuLocation') + ' *'}
                        >
                          <MuiSelect
                            multiple={true}
                            fullWidth
                            displayEmpty={false}
                            defaultValue={[]}
                            value={selectedLocations}
                            onChange={handleTestLocationChange}
                            renderValue={(selected) =>
                              selected?.length > 0 && selected?.length < 6
                                ? selected?.join(', ')
                                : selected?.length + ' ' + t('LBLItemsSelected')
                            }
                            open={locationSelectOpen}
                            onOpen={() => setLocationSelectOpen(true)}
                            onClose={() => setLocationSelectOpen(false)}
                            MenuProps={{
                              PaperProps: { sx: { boxShadow: shadows[3] } },
                              onClose: (event, reason) => {
                                if (
                                  reason === 'backdropClick' ||
                                  reason === 'escapeKeyDown'
                                ) {
                                  setLocationSelectOpen(false);
                                } else {
                                  // item click or other reasons: keep it open
                                  setLocationSelectOpen(true);
                                }
                              },
                            }}
                          >
                            <MenuItem
                              value="all"
                              divider
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={isAllSelectedStations}
                                indeterminate={
                                  selectedLocations?.length > 0 &&
                                  selectedLocations?.length < stations?.length
                                }
                              />
                              <ListItemText primary={t('LBLALLLocations')} />
                            </MenuItem>
                            {stations?.map((station) => (
                              <MenuItem
                                key={station?.locationId}
                                value={station?.locationId}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Checkbox
                                  size="small"
                                  checked={
                                    selectedLocations?.indexOf(
                                      station?.locationId
                                    ) > -1
                                  }
                                />
                                <ListItemText
                                  primaryTypographyProps={{ fontSize: '12px' }}
                                >
                                  {station?.locationId}: {station?.locationName}
                                </ListItemText>
                              </MenuItem>
                            ))}
                          </MuiSelect>
                        </DownloadCenterFieldRow>

                        {/* Charging Type */}
                        <DownloadCenterFieldRow
                          label={t('LBLChargingTypeMode') + ' *'}
                        >
                          <TextField
                            select
                            fullWidth
                            id="chargingTypeMode"
                            onChange={handleChargingTypeModeChange}
                            value={chargingTypeModeSelected}
                            name="ChargingTypeMode"
                          >
                            {chargingTypeModes.map((method, index) => (
                              <MenuItem
                                size="small"
                                key={index}
                                value={method.value}
                              >
                                {t(method.translationKey)}
                              </MenuItem>
                            ))}
                          </TextField>
                        </DownloadCenterFieldRow>

                        {/* Dates */}
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale="de"
                        >
                          <DownloadCenterFieldRow
                            label={t('fromDatePosting') + ' *'}
                          >
                            <DatePicker
                              value={values.startDate}
                              onChange={(newValue) =>
                                handleFromDatePostingChange(
                                  newValue,
                                  setFieldValue
                                )
                              }
                              renderInput={(params) => (
                                <Field
                                  component={TextField}
                                  {...params}
                                  name="startDate"
                                  required
                                  fullWidth
                                  margin="none"
                                  autoComplete="off"
                                  helperText={errors.startDate}
                                  error={Boolean(errors.startDate)}
                                  inputProps={{
                                    ...params.inputProps,
                                    placeholder: 'TT.MM.JJJJ',
                                  }}
                                />
                              )}
                            />
                          </DownloadCenterFieldRow>

                          <DownloadCenterFieldRow
                            label={t('endPostingDate') + ' *'}
                          >
                            <DatePicker
                              value={values.endDate}
                              maxDate={dayjs()}
                              onChange={(newValue) =>
                                handleUntilDatePostingChange(
                                  newValue,
                                  setFieldValue
                                )
                              }
                              renderInput={(params) => (
                                <Field
                                  component={TextField}
                                  {...params}
                                  name="endDate"
                                  required
                                  fullWidth
                                  margin="none"
                                  autoComplete="off"
                                  helperText={errors.endDate}
                                  error={Boolean(errors.endDate)}
                                  inputProps={{
                                    ...params.inputProps,
                                    placeholder: 'TT.MM.JJJJ',
                                  }}
                                />
                              )}
                            />
                          </DownloadCenterFieldRow>
                        </LocalizationProvider>
                      </CardContent>
                    </View>
                  </Grid>

                  {/* RIGHT COLUMN */}
                  <Grid item xs={12} md={6}>
                    <View>
                      <CardContent>
                        <DownloadCenterFieldRow label={t('MenuLocation')}>
                          {selectedLocations?.length > 0 &&
                            selectedLocations?.length < 6 && (
                              <div>
                                {formatLocationIdWithName(
                                  stations,
                                  selectedLocations
                                )}
                              </div>
                            )}
                          {selectedLocations?.length > 5 && (
                            <div>
                              {selectedLocations?.length}{' '}
                              {t('LBLItemsSelected')}
                            </div>
                          )}
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow
                          label={t('LBLChargingTypeMode')}
                        >
                          <div>
                            {translateChargingType(
                              t,
                              chargingTypeModes,
                              chargingTypeModeSelected
                            )}
                          </div>
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('fromDatePosting')}>
                          <div>
                            {new Date(values.startDate).toLocaleDateString(
                              'de-DE'
                            )}
                          </div>
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('endPostingDate')}>
                          <div>
                            {new Date(values.endDate).toLocaleDateString(
                              'de-DE'
                            )}
                          </div>
                        </DownloadCenterFieldRow>

                        <DownloadCenterFieldRow label={t('Format')}>
                          <div>{exportType}</div>
                        </DownloadCenterFieldRow>

                        <Box
                          sx={{ textAlign: 'center', mt: { xs: 6, md: 12 } }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={
                              ButtonDownloadHistoryIsSubmitting ||
                              selectedLocations?.length == 0 ||
                              errors?.startDate ||
                              errors?.endDate
                            }
                          >
                            {t('BTNDownload')}
                          </Button>
                        </Box>
                      </CardContent>
                    </View>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <Suspense fallback={<div />}>
        <FailFeedback
          open={error}
          title={'Fehler'}
          severity="warning"
          message={
            <>
              <Typography align="center">
                {t('LBLSomethingwentWrong')}
              </Typography>
            </>
          }
          handleClose={() => {
            setError(false);
            setError204(false);
          }}
        />
        <FailFeedback
          open={error204}
          title={t('noinfoFound')}
          severity="warning"
          message={
            <>
              <Typography align="center">{t('LBLNoDataDC')}</Typography>
            </>
          }
          handleClose={() => {
            setError(false);
            setError204(false);
          }}
        />
        <FailFeedback
          open={error404}
          title={t('title404')}
          severity="warning"
          message={
            <>
              <Typography align="center">{t('body404')}</Typography>
            </>
          }
          handleClose={() => {
            setError(false);
            setError204(false);
            setError404(false);
          }}
        />
      </Suspense>
    </>
  );
}
export default DownloadChargingHistory;
